/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Skeleton
} from '@mui/material';
import {
  People,
  DirectionsCar,
  AttachMoney,
  Star,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import { useState, useEffect, useCallback } from 'react';
import { ToggleButton, ToggleButtonGroup, CircularProgress } from '@mui/material';
import { getAnalytics, getDrivers, getTrips, getDriverRevenueLeaderboard, type DriverRevenueRow } from '../services/graphql';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/format';

function Analytics() {
  const [analytics, setAnalytics] = useState<any>(null);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [trips, setTrips] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { hasRole } = useAuth();

  const canViewDrivers = hasRole('SuperAdmin', 'Operations', 'CustomerService', 'TechSupport');
  const canViewRevenue = hasRole('SuperAdmin', 'Operations', 'Finance');

  // Driver revenue leaderboard (windowed + paginated)
  const [lbItems, setLbItems] = useState<DriverRevenueRow[]>([]);
  const [lbPeriod, setLbPeriod] = useState<'WEEK' | 'MONTH'>('MONTH');
  const [lbOrder, setLbOrder] = useState<'TOP' | 'LOW'>('TOP');
  const [lbNextToken, setLbNextToken] = useState<string | null>(null);
  const [lbTotal, setLbTotal] = useState(0);
  const [lbLoading, setLbLoading] = useState(false);

  const fetchLeaderboard = useCallback(async (reset: boolean) => {
    if (!canViewRevenue) return;
    setLbLoading(true);
    try {
      const res = await getDriverRevenueLeaderboard({
        period: lbPeriod,
        order: lbOrder,
        limit: 10,
        nextToken: reset ? null : lbNextToken,
      });
      setLbItems(prev => reset ? res.items : [...prev, ...res.items]);
      setLbNextToken(res.nextToken);
      setLbTotal(res.total);
    } catch (e) {
      console.error('Failed to load driver revenue leaderboard:', e);
    } finally {
      setLbLoading(false);
    }
  }, [canViewRevenue, lbPeriod, lbOrder, lbNextToken]);

  // Reload from the top whenever the window or order changes
  useEffect(() => { setLbItems([]); setLbNextToken(null); fetchLeaderboard(true); /* eslint-disable-next-line */ }, [lbPeriod, lbOrder, canViewRevenue]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsResult, tripsResult, driversResult] = await Promise.allSettled([
          getAnalytics(),
          getTrips(),
          canViewDrivers ? getDrivers() : Promise.resolve([]),
        ]);

        if (analyticsResult.status === 'fulfilled') {
          setAnalytics(analyticsResult.value);
        } else {
          console.error('Failed to load analytics:', analyticsResult.reason);
        }

        if (tripsResult.status === 'fulfilled') {
          setTrips(tripsResult.value);
        } else {
          console.error('Failed to load trips:', tripsResult.reason);
        }

        if (driversResult.status === 'fulfilled') {
          setDrivers(driversResult.value);
        } else {
          console.error('Failed to load drivers:', driversResult.reason);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [canViewDrivers]);

  if (loading || !analytics) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">
            Analytics & Reports
          </Typography>
          <Skeleton variant="rectangular" width={150} height={36} />
        </Box>

        {/* Key Metrics Skeleton */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[...Array(4)].map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                <CardContent>
                  <Skeleton variant="text" width={100} height={20} />
                  <Skeleton variant="text" width={80} height={40} />
                  <Skeleton variant="text" width={120} height={16} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Revenue Chart Skeleton */}
          <Grid item xs={12} md={8}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Skeleton variant="text" width={200} height={30} />
                <Skeleton variant="rectangular" width="100%" height={300} />
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Methods Skeleton */}
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Skeleton variant="text" width={150} height={30} />
                <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto' }} />
              </CardContent>
            </Card>
          </Grid>

          {/* Hourly Revenue Skeleton */}
          <Grid item xs={12} md={8}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Skeleton variant="text" width={250} height={30} />
                <Skeleton variant="rectangular" width="100%" height={300} />
              </CardContent>
            </Card>
          </Grid>

          {/* Top Drivers Skeleton */}
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Skeleton variant="text" width={180} height={30} />
                {[...Array(5)].map((_, i) => (
                  <Box key={i} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Skeleton variant="text" width={100} />
                    <Skeleton variant="text" width={40} />
                    <Skeleton variant="text" width={30} />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  const { totalTrips, totalRevenue, totalUsers, averageRating, tripsByHour, revenueByDay } = analytics;
  const completedTrips = trips.filter(trip => trip.status?.toUpperCase() === 'COMPLETED');
  const averageTripValue = completedTrips.length > 0 ? completedTrips.reduce((sum, trip) => sum + (trip.finalFare || trip.fareEstimate || 0), 0) / completedTrips.length : 0;
  const totalDistance = completedTrips.reduce((sum, trip) => sum + (trip.distance || 0), 0);
  
  const paymentMethodData = [
    { method: 'Cash', count: Math.round(analytics.cashRevenue ?? 0), color: '#ed6c02' },
    { method: 'Non-Cash', count: Math.round(analytics.nonCashRevenue ?? 0), color: '#1565c0' },
  ].filter(d => d.count > 0);

  const hourlyRevenue = tripsByHour.map((item: any) => ({
    ...item,
    revenue: item.trips * averageTripValue
  }));

  const MetricCard = ({ title, value, icon, color }: any) => (
    <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4">
              {value}
            </Typography>
          </Box>
          <Box sx={{ color, fontSize: 40 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4">
          Analytics &amp; Reports
        </Typography>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Revenue"
            value={formatCurrency(totalRevenue)}
            icon={<AttachMoney />}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Trips"
            value={totalTrips.toLocaleString()}
            icon={<DirectionsCar />}
            color="#2196f3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Users"
            value={totalUsers}
            icon={<People />}
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Trip Value"
            value={formatCurrency(averageTripValue)}
            icon={<Star />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Revenue Trend */}
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue Trend (Last 7 Days)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip formatter={(value) => [formatCurrency(Number(value)), 'Revenue']} />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#4caf50" 
                    fill="#4caf50" 
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Methods */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Methods
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={paymentMethodData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                    label={({ method }) => `${method}`}
                  >
                    {paymentMethodData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [formatCurrency(Number(value)), 'Revenue']} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Hourly Revenue */}
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hourly Revenue Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={hourlyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip formatter={(value) => [formatCurrency(Number(value)), 'Revenue']} />
                  <Bar dataKey="revenue" fill="#2196f3" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Driver Revenue Leaderboard — windowed + paginated */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
                <Typography variant="h6">Driver Revenue</Typography>
                <Box display="flex" gap={1}>
                  <ToggleButtonGroup size="small" exclusive value={lbPeriod} onChange={(_, v) => v && setLbPeriod(v)}>
                    <ToggleButton value="WEEK">Week</ToggleButton>
                    <ToggleButton value="MONTH">Month</ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup size="small" exclusive value={lbOrder} onChange={(_, v) => v && setLbOrder(v)}>
                    <ToggleButton value="TOP">Top</ToggleButton>
                    <ToggleButton value="LOW">Low</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
              {!canViewRevenue ? (
                <Typography color="textSecondary" variant="body2" sx={{ py: 2 }}>
                  Revenue data is not available for your role.
                </Typography>
              ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Driver</TableCell>
                        <TableCell align="right">Trips</TableCell>
                        <TableCell align="right">Gross</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lbItems.length === 0 && !lbLoading ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography color="textSecondary" variant="body2">No revenue data</Typography>
                          </TableCell>
                        </TableRow>
                      ) : lbItems.map((d, index) => (
                        <TableRow key={d.driverId}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              #{index + 1} {d.driverName || d.driverId.slice(0, 8)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Cash {formatCurrency(d.cashRevenue)} · Non-cash {formatCurrency(d.nonCashRevenue)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{d.tripCount}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">{formatCurrency(d.grossRevenue)}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box display="flex" justifyContent="center" alignItems="center" mt={1}>
                  {lbLoading ? (
                    <CircularProgress size={20} />
                  ) : lbNextToken ? (
                    <Button size="small" onClick={() => fetchLeaderboard(false)}>Load more</Button>
                  ) : lbItems.length > 0 ? (
                    <Typography variant="caption" color="textSecondary">Showing {lbItems.length} of {lbTotal}</Typography>
                  ) : null}
                </Box>
              </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Trip Status Distribution */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Trip Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={[
                  { status: 'Completed', count: trips.filter(t => t.status?.toUpperCase() === 'COMPLETED').length },
                  { status: 'Active', count: trips.filter(t => t.status?.toUpperCase() === 'IN_PROGRESS').length },
                  { status: 'Cancelled', count: trips.filter(t => t.status?.toUpperCase() === 'CANCELLED').length }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="status" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#ff9800" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Key Statistics */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Key Statistics
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Total Distance Covered</Typography>
                  <Typography fontWeight="bold">{totalDistance.toFixed(1)} km</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Average Trip Distance</Typography>
                  <Typography fontWeight="bold">
                    {completedTrips.length > 0 ? (totalDistance / completedTrips.length).toFixed(1) : '0'} km
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Completion Rate</Typography>
                  <Typography fontWeight="bold" color="success.main">
                    {trips.length > 0 ? ((completedTrips.length / trips.length) * 100).toFixed(1) : '0'}%
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Cancellation Rate</Typography>
                  <Typography fontWeight="bold" color="error.main">
                    {trips.length > 0 ? ((trips.filter(t => t.status?.toUpperCase() === 'CANCELLED').length / trips.length) * 100).toFixed(1) : '0'}%
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Average Driver Rating</Typography>
                  <Typography fontWeight="bold">
                    ⭐ {averageRating}
                  </Typography>
                </Box>
              </Box>

            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Analytics;